import errorPage from '@design-system/data/errorPage.json'
import type { ErrorPage } from '@integration-layer/ampliance/schemas/error-page-schema.localized'

/**
 * Retrieves and configures error page content based on a delivery key.
 *
 * @param {number} deliveryKey - A numeric identifier for the specific error page.
 * @returns {Promise<{ data: Ref<ErrorPage> }>} An object containing the error page data.
 *
 * @description
 * This function fetches error page content with the following behavior:
 * - Supports localized error page content
 * - Falls back to default error page data if fetch fails
 * - Uses CMS API to retrieve dynamic content
 * - Supports optional query parameters for content variations
 *
 * @example
 * // Fetch error page content for a specific error type
 * const { data: errorPageContent } = await useErrorPage(404)
 *
 * @remarks
 * - Requires active internationalization (i18n) setup
 * - Supports dynamic content retrieval
 * - Provides fallback content from a static JSON file
 */
export async function useErrorPage(deliveryKey: number | string) {
  const localePath = useLocalePath()
  const { ts } = useI18n()

  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey: `error-page-${deliveryKey}`,
  }

  const data = ref<ErrorPage>()

  const { data: responseData, error } = await useFetch(
    '/api/cms/getErrorPageContent',
    {
      query,
    }
  )

  if (error.value || !responseData.value) {
    data.value = {
      ...errorPage,
      title: ts(`errorPage.${deliveryKey}.title`),
      subtitle: ts(`errorPage.${deliveryKey}.subTitle`),
      cta1Text: ts(`errorPage.${deliveryKey}.backPrevPageText`),
      cta2Text: ts(`errorPage.${deliveryKey}.backHomeText`),
    }
  } else {
    data.value = responseData.value
  }

  return {
    data,
  }
}
